/**
 * boardsApi — Customer Portal client for /boards endpoints (FEAT-BOARDS-0001).
 * Matches the portal-wide convention: REACT_APP_API_URL base + x-api-key +
 * x-access-token headers (per utils/DefaultFunction.js getApiHeaders).
 * NOT axios src/api/client.js — that wrapper prepends `/api` which RRS doesn't
 * use for portal routes (caused a 404 on the first attempt).
 */
import axios from 'axios';
import { getApiHeaders } from '../../utils/DefaultFunction';

const BASE = process.env.REACT_APP_API_URL || '';

const request = async (method, path, opts = {}) => {
  // opts.base lets one call point at a different backend (e.g. cohorts, which
  // now lives on its own service) without moving every other Boards endpoint.
  const url = `${opts.base ?? BASE}${path}`;
  const r = await axios({
    method,
    url,
    data: opts.data,
    params: opts.params,
    headers: getApiHeaders(),
    timeout: opts.timeout || 130000,
  });
  return r.data;
};

export const boardsApi = {
  /**
   * List all boards visible to the current user (personal owned + all team).
   * @returns {Promise<{success: boolean, data: Array, meta: {count: number}}>}
   */
  list(opts = {}) {
    const params = {};
    if (opts.scope) params.scope = opts.scope;
    return request('GET', '/boards', { params });
  },

  /** Fetch a single board with tiles embedded. */
  get(id) {
    return request('GET', `/boards/${id}`);
  },

  /**
   * Render a tile against live data.
   * @returns {Promise<{success: boolean, data: {tile_id, type, source, data, error?, meta}}>}
   */
  renderTile(boardId, tileId, opts = {}) {
    const body = {};
    if (opts.time_frame) body.time_frame = opts.time_frame;
    return request('POST', `/boards/${boardId}/tiles/${tileId}/render`, { data: body });
  },

  /**
   * Pre-warm cohort-member Redis caches for every distinct cohort_scope on
   * the board. Call this ONCE right after fetching the board, BEFORE firing
   * the per-tile renders. Subsequent tile renders hit hot caches → no 60s
   * timeouts under parallel load.
   */
  prewarm(boardId) {
    return request('POST', `/boards/${boardId}/prewarm`, { data: {} });
  },

  // ── Mutations ──
  createBoard(payload) { return request('POST', '/boards', { data: payload }); },
  updateBoard(id, payload) { return request('PUT', `/boards/${id}`, { data: payload }); },
  deleteBoard(id) { return request('DELETE', `/boards/${id}`); },
  cloneBoard(id) { return request('POST', `/boards/${id}/clone`); },

  createTile(boardId, payload) { return request('POST', `/boards/${boardId}/tiles`, { data: payload }); },
  updateTile(boardId, tileId, payload) { return request('PUT', `/boards/${boardId}/tiles/${tileId}`, { data: payload }); },
  deleteTile(boardId, tileId) { return request('DELETE', `/boards/${boardId}/tiles/${tileId}`); },

  // ── Lookups for editor pickers ──
  // All three lookups normalize the wrapped response shape into a plain array
  // so the UI is shape-safe.
  async listCohorts() {
    try {
      const r = await request('GET', '/cohorts', { params: { limit: 100, page: 1, status: 'active' }, base: process.env.REACT_APP_COHORT });
      const arr = r?.data?.cohorts || r?.data || [];
      return { data: Array.isArray(arr) ? arr : [] };
    } catch (e) { return { data: [] }; }
  },

  // POST /fetch-unique-events → { data: { events: [{display_name, name}, ...] } }
  // Short timeout (don't keep the Event picker disabled for 130s on a slow/erroring
  // backend) and surface the failure — a silent catch is what made the "Event dropdown
  // stays disabled" issue undiagnosable on live (empty events => EventsPicker disabled).
  async listEvents() {
    try {
      const r = await request('POST', '/fetch-unique-events', { data: {}, timeout: 20000 });
      const arr = r?.data?.events || [];
      if (!Array.isArray(arr) || arr.length === 0) {
        console.warn('[boards] listEvents: no events returned', { shape: r && Object.keys(r) });
      }
      return { data: Array.isArray(arr) ? arr : [] };
    } catch (e) {
      console.error('[boards] listEvents failed:', e?.response?.status, e?.response?.data || e?.message);
      return { data: [], error: e?.response?.status || e?.message };
    }
  },

  // POST /fetch-unique-attributes {event} → { data: { event, attributes: [{name,type,...}, ...] } }
  async listEventAttributes(event) {
    if (!event) return { data: [] };
    try {
      const r = await request('POST', '/fetch-unique-attributes', { data: { event } });
      const arr = r?.data?.attributes || [];
      return { data: Array.isArray(arr) ? arr : [] };
    } catch (e) { return { data: [] }; }
  },

  // GET /user/list → { data: [{ id, username, email, name, ... }], require_2fa_all }.
  // Tenant user directory for the board-sharing picker (Bug-006). Normalizes to a
  // plain array of { id, name, email, username } so the picker stays shape-safe.
  async listUsers() {
    try {
      const r = await request('GET', '/user/list');
      const arr = Array.isArray(r?.data) ? r.data : [];
      return {
        data: arr
          .filter((u) => u && (u.id !== undefined && u.id !== null))
          .map((u) => ({
            id: String(u.id),
            name: u.name || u.full_name || u.username || u.email || `User ${u.id}`,
            email: u.email || '',
            username: u.username || '',
          })),
      };
    } catch (e) {
      return { data: [], error: e?.response?.status || e?.message };
    }
  },

  // GET /contacts/customer-profile-keys → { success, data: [{ key, ... }] }.
  // Populates the Profile Breakdown "Profile key" dropdown (Bug-019).
  async listProfileKeys() {
    try {
      const r = await request('GET', '/contacts/customer-profile-keys');
      const arr = Array.isArray(r?.data) ? r.data : [];
      return { data: arr.map((x) => (typeof x === 'string' ? x : x.key)).filter(Boolean) };
    } catch (e) { return { data: [] }; }
  },
};
